import { useRef } from 'react'
import type { Editor } from '@tiptap/core'
import { useEditor as useTiptapEditor } from '@tiptap/react'
import { useRouter } from 'next/router'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useMentionSuggestion } from 'modules/community/components/editor/extensions/mention-suggestion'
import { createPostExtensions } from 'modules/community/components/editor/extensions/post-extensions'
import { docToHtml, htmlToDoc } from 'modules/community/utils/post-html'

interface UseEditorProps {
  contentHtml?: string
  mentions?: MentionInterface[]
  onHtmlChange?: (html: string) => void
}

interface UseEditorResult {
  editor: Editor | null
  insertEmoji: (emoji: string) => void
  clearContent: () => void
}

export const useEditor = ({
  contentHtml,
  mentions = [],
  onHtmlChange,
}: UseEditorProps): UseEditorResult => {
  const { query } = useRouter()
  const communityPathRef = useRef(query.communityPath)
  communityPathRef.current = query.communityPath
  const onHtmlChangeRef = useRef(onHtmlChange)
  onHtmlChangeRef.current = onHtmlChange
  const mentionSuggestion = useMentionSuggestion(() => {
    const path = communityPathRef.current
    return path ? { communityPath: path } : undefined
  })

  const editor = useTiptapEditor({
    extensions: createPostExtensions({ mentionSuggestion }),
    content: htmlToDoc(contentHtml ?? '', mentions),
    immediatelyRender: false,
    editorProps: {
      attributes: {
        dir: 'auto',
        class:
          'max-h-[300px] min-h-[200px] overflow-x-hidden overflow-y-auto rounded-[5px] border border-text-secondary/30 bg-transparent p-[15px] text-start outline-none focus:border-border-accent [&_a]:text-text-link [&_a]:underline',
      },
    },
    onCreate: ({ editor: ed }) => {
      onHtmlChangeRef.current?.(docToHtml(ed.getJSON()))
    },
    onUpdate: ({ editor: ed }) => {
      onHtmlChangeRef.current?.(docToHtml(ed.getJSON()))
    },
  })

  return {
    editor,
    insertEmoji: (emoji: string) => {
      editor?.chain().focus().insertContent(emoji).run()
    },
    clearContent: () => {
      editor?.commands.clearContent(true)
      onHtmlChangeRef.current?.('<br>')
    },
  }
}
